import { useCallback } from "react";
import { Moon, Sun } from "lucide-react";

import { useTheme } from "@/hooks/use-theme";
import { cn } from "@/lib/utils";

/**
 * Theme switch. The button uses the familiar sun/moon glyphs, so anyone can
 * tell at a glance that it toggles between the light theme and the dark Deep
 * Lagoon theme — a sun when it currently shows the dark theme, a moon when it
 * currently shows the light theme.
 *
 * The click point is handed to `toggleTheme` so the view-transition reveal can
 * grow from the control the user actually pressed.
 */
export function ThemeToggle({ className }) {
  const { theme, toggleTheme } = useTheme();
  const isLagoon = theme === "lagoon";

  const handleClick = useCallback(
    (event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      toggleTheme({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
    },
    [toggleTheme],
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={isLagoon ? "Switch to light theme" : "Switch to dark theme"}
      title={isLagoon ? "Switch to light theme" : "Switch to dark theme"}
      aria-pressed={isLagoon}
      className={cn(
        "inline-flex size-11 min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full",
        "text-text transition-colors hover:bg-surface-hover",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
        className,
      )}
    >
      {isLagoon ? (
        <Sun className="size-5" aria-hidden="true" />
      ) : (
        <Moon className="size-5" aria-hidden="true" />
      )}
    </button>
  );
}
